import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Descriptions, Image, Skeleton, Tag } from "antd";
import { MDBIcon } from "mdbreact";
import { Button } from "evergreen-ui";
import Firebase from "../../Firebase/Firebase";
import { useListVals } from "../../Firebase/databaseHooks";
import { getDatabase, ref } from "firebase/database";
import {
	getDownloadURL,
	getStorage,
	listAll,
	ref as storageRef,
} from "firebase/storage";
import moment from "moment";
import {
	classifyInteractionMediaName,
	getInteractionInlineMedia,
	getInteractionMetadata,
	getInteractionParticipantCounts,
	getInteractionTypeLabel,
	getUserName,
	normalizeBoolean,
} from "../../Firebase/dataCompat";

const { Item } = Descriptions;
const storage = getStorage(Firebase);
const db = getDatabase(Firebase);

function getReferenceLabel(items, id, idFields, labelFields, fallback) {
	if (!id) return fallback;
	const match = (items || []).find((item) =>
		idFields.some((field) => item?.[field] === id)
	);
	if (!match) return fallback;
	return labelFields.map((field) => match?.[field]).find(Boolean) || fallback;
}

function mergeMedia(inlineMedia, discoveredMedia) {
	const inlineKinds = new Set(inlineMedia.map((item) => item.kind));
	const seenUrls = new Set();
	return [...inlineMedia, ...discoveredMedia.filter((item) => !inlineKinds.has(item.kind))]
		.filter((item) => {
			if (!item.url || seenUrls.has(item.url)) return false;
			seenUrls.add(item.url);
			return true;
		});
}

function isImageUrl(url) {
	return /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(String(url || ""));
}

const ViewInteraction = ({ interaction }) => {
	const [organizationArray, organizationsLoading, organizationsError] = useListVals(
		ref(db, "talmidsdb/organisations")
	);
	const [communityArray, communitiesLoading, communitiesError] = useListVals(
		ref(db, "talmidsdb/communities")
	);
	const [userArray, usersLoading, usersError] = useListVals(ref(db, "talmidsdb/users"));
	const [lessonArray, lessonsLoading, lessonsError] = useListVals(ref(db, "talmidsdb/lessons"));
	const [resolvedMedia, setResolvedMedia] = useState([]);
	const [mediaLoading, setMediaLoading] = useState(Boolean(interaction));
	const [mediaError, setMediaError] = useState(false);
	const [loadedMediaInteractionId, setLoadedMediaInteractionId] = useState(null);

	const metadata = useMemo(() => getInteractionMetadata(interaction), [interaction]);
	const participantCounts = useMemo(
		() => getInteractionParticipantCounts(interaction),
		[interaction]
	);

	useEffect(() => {
		let cancelled = false;
		const inlineMedia = getInteractionInlineMedia(interaction);
		const interactionId = metadata.interactionId;

		setResolvedMedia([]);
		setMediaError(false);
		setMediaLoading(Boolean(interactionId));

		if (!interactionId) {
			setResolvedMedia(inlineMedia);
			setLoadedMediaInteractionId(null);
			setMediaLoading(false);
			return () => {
				cancelled = true;
			};
		}

		const loadStorageMedia = async () => {
			const folders = [
				`talmidsdb/Interactions/${interactionId}`,
				`System/Interactions/${interactionId}`,
			];
			const folderResults = await Promise.allSettled(
				folders.map((folder) => listAll(storageRef(storage, folder)))
			);
			const itemRefs = folderResults.flatMap((result) =>
				result.status === "fulfilled" ? result.value.items : []
			);
			const urlResults = await Promise.allSettled(
				itemRefs.map(async (itemRef) => ({
					kind: classifyInteractionMediaName(itemRef.name),
					name: itemRef.name,
					url: await getDownloadURL(itemRef),
				}))
			);
			const discoveredMedia = urlResults
				.filter((result) => result.status === "fulfilled")
				.map((result) => result.value);
			const failed =
				folderResults.every((result) => result.status === "rejected") ||
				urlResults.some((result) => result.status === "rejected");

			if (!cancelled) {
				setResolvedMedia(mergeMedia(inlineMedia, discoveredMedia));
				setMediaError(failed);
				setLoadedMediaInteractionId(interactionId);
				setMediaLoading(false);
			}
		};

		loadStorageMedia().catch(() => {
			if (!cancelled) {
				setResolvedMedia(inlineMedia);
				setMediaError(true);
				setLoadedMediaInteractionId(interactionId);
				setMediaLoading(false);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [interaction, metadata.interactionId]);

	if (!interaction) {
		return <Alert message="No interaction data available" type="warning" />;
	}

	const loading =
		organizationsLoading ||
		communitiesLoading ||
		usersLoading ||
		lessonsLoading ||
		mediaLoading ||
		loadedMediaInteractionId !== metadata.interactionId;
	if (loading) {
		return (
			<Card title="Interaction Details" variant="borderless">
				<Skeleton active title paragraph={{ rows: 12 }} />
			</Card>
		);
	}

	const lookupError = organizationsError || communitiesError || usersError || lessonsError;
	const lessonName = getReferenceLabel(
		lessonArray,
		metadata.lessonId,
		["id", "lessonID"],
		["lessonName", "name"],
		"Unknown lesson"
	);
	const organisationName = getReferenceLabel(
		organizationArray,
		metadata.organisationId,
		["id", "orgID", "organisationID"],
		["name", "organisationName"],
		"Unknown organization"
	);
	const communityName = getReferenceLabel(
		communityArray,
		metadata.communityId,
		["id", "communityID"],
		["name", "communityName"],
		"Unknown community"
	);
	const communicatedWith = metadata.communicatedWithId ?
		getUserName(userArray, metadata.communicatedWithId, metadata.communicatedWithName || "Unknown user") :
		metadata.communicatedWithName || "Not specified";
	const formattedDate = metadata.date && moment(metadata.date).isValid() ?
		moment(metadata.date).format("MMMM Do, YYYY HH:mm") :
		"Not specified";
	const needsAttention = normalizeBoolean(
		interaction.needsAttention ?? interaction.needAttention
	);

	return (
		<div className="interaction-details-viewer">
			<Card title="Interaction Details" variant="borderless">
				{lookupError && (
					<Alert
						message="Some reference details could not be loaded. Available report data is shown below."
						type="warning"
						className="mb-3"
					/>
				)}
				<Descriptions column={1} bordered>
					<Item label="Type"><Tag color="blue">{getInteractionTypeLabel(interaction)}</Tag></Item>
					<Item label="Lesson">{lessonName}</Item>
					<Item label="Organization">{organisationName}</Item>
					<Item label="Community">{communityName}</Item>
					<Item label="Communicated With">{communicatedWith}</Item>
					<Item label="Worker">{getUserName(userArray, metadata.workerId, "Unknown user")}</Item>
					<Item label="Date">{formattedDate}</Item>
					<Item label="Participants">
						<div className="participant-stats">
							<span><strong>Males:</strong> {participantCounts.males}</span>
							<span><strong>Females:</strong> {participantCounts.females}</span>
							<span><strong>Youths:</strong> {participantCounts.youths}</span>
							<span><strong>Disabled:</strong> {participantCounts.disabled}</span>
							<span><strong>Elderly:</strong> {participantCounts.elderly}</span>
						</div>
					</Item>
					<Item label="Needs Attention">
						<Tag color={needsAttention ? "red" : "green"}>{needsAttention ? "Yes" : "No"}</Tag>
					</Item>
					<Item label="Notes"><div className="notes-content">{interaction.notes || "No notes provided"}</div></Item>
					<Item label="Commitments"><div className="notes-content">{interaction.commitments || "No commitments recorded"}</div></Item>
				</Descriptions>

				<div className="media-section">
					<h3>Media</h3>
					{mediaError && (
						<Alert message="Some media files could not be loaded." type="warning" className="mb-3" />
					)}
					{resolvedMedia.length ? (
						<div className="media-grid">
							{resolvedMedia.map((mediaItem, index) => (
								<div className="media-item" key={`${mediaItem.url}-${index}`}>
									<h4>{mediaItem.name || "Interaction Media"}</h4>
									{isImageUrl(mediaItem.url) && (
										<Image width={200} src={mediaItem.url} alt={mediaItem.name || mediaItem.kind} className="media-image" />
									)}
									<Button className="mt-2">
										<a href={mediaItem.url} download target="_blank" rel="noreferrer">
											<MDBIcon icon="download" /> Download
										</a>
									</Button>
								</div>
							))}
						</div>
					) : (
						<Alert message="No media available for this interaction" type="info" />
					)}
				</div>
			</Card>

			<style>{`
				.interaction-details-viewer { max-width: 800px; margin: 0 auto; }
				.participant-stats, .media-grid { display: flex; flex-wrap: wrap; gap: 16px; }
				.notes-content { white-space: pre-wrap; background: #f5f5f5; padding: 12px; border-radius: 4px; }
				.media-section { margin-top: 24px; }
				.media-item { display: flex; flex-direction: column; align-items: flex-start; }
				.media-image { border: 1px solid #ddd; border-radius: 4px; }
			`}</style>
		</div>
	);
};

export default ViewInteraction;
